import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/field";
import { MenuTrigger } from "@/components/ui/menu";
import { Menu, MenuItem, MenuPopover } from "@/components/ui/menu";
import type { NewsAlertSortOrder } from "@/data-access/news-alerts/queries/news-alerts-queries";
import { cn } from "@/lib/utils";
import {
  deleteNewsAlertSection,
  editNewsAlertSection,
  openCreateNewsAlertSectionModal,
} from "@/store/news-alerts/news-alerts.actions";
import { updateCreateEditNewsAlert } from "@/store/news-alerts/news-alerts.actions";
import useNewsAlertsStore, {
  type AlertSection,
} from "@/store/news-alerts/news-alerts.slice";
import { CaretDownIcon, CheckIcon, PlusIcon } from "@phosphor-icons/react";
import { useCallback } from "react";
import {
  ARTICLE_LIMIT_OPTIONS,
  SORT_ORDER_LABELS,
  SORT_ORDER_OPTIONS,
} from "../newsAlertsHelpers";

const NewsAlertsSectionListItem = ({
  borderLess = false,
  children,
}: {
  borderLess?: boolean;
  children: React.ReactNode;
}) => (
  <li
    className={cn(
      "flex flex-row gap-2 justify-between items-center py-2",
      !borderLess ? "border-b border-gray-200" : "",
    )}
  >
    {children}
  </li>
);

const NewsAlertsSectionsList = () => {
  const createEditNewsAlert = useNewsAlertsStore(
    (state) => state.createEditNewsAlert,
  );

  const { sections } = createEditNewsAlert;

  const sectionList = Array.from(sections.values());

  // ArticleOptions are a global control which applies to all sections
  const renderArticleOptions = useCallback(() => {
    if (sectionList.length === 0) {
      return null;
    }

    const { articleLimit, sortOrder } = sectionList[0] as AlertSection;

    return (
      <div className="flex flex-row flex-wrap gap-2">
        <MenuTrigger>
          <div className="flex flex-col gap-1 w-1/4 min-w-72">
            <Label className="text-xs text-gray-900">Article Limit</Label>
            <Button
              size={"sm"}
              variant={"outline"}
              className={"flex justify-between w-full"}
            >
              {articleLimit ? articleLimit : ARTICLE_LIMIT_OPTIONS[0]?.label}
              <CaretDownIcon />
            </Button>
          </div>

          <MenuPopover className={"w-(--trigger-width) min-h-min"}>
            <Menu>
              {ARTICLE_LIMIT_OPTIONS.map((option) => (
                <MenuItem
                  key={`article-limit-drop-item-${option.value}`}
                  className={"text-xs tracking-wide pr-2"}
                  onAction={() =>
                    updateCreateEditNewsAlert({
                      ...createEditNewsAlert,
                      sections: new Map(
                        sectionList.map((section) => [
                          section.id,
                          {
                            ...section,
                            articleLimit: option.value as number, // Apply the article limit to all sections
                          },
                        ]),
                      ),
                    })
                  }
                >
                  {option.label}
                  {articleLimit === option.value && (
                    <CheckIcon className={"ml-auto"} />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </MenuPopover>
        </MenuTrigger>

        <MenuTrigger>
          <div className="flex flex-col gap-1 w-1/4 min-w-72">
            <Label className="text-xs text-gray-900">Sort Order</Label>
            <Button
              size={"sm"}
              variant={"outline"}
              className={"flex justify-between w-full"}
            >
              {sortOrder
                ? SORT_ORDER_LABELS[sortOrder]
                : SORT_ORDER_OPTIONS[0]?.label}
              <CaretDownIcon />
            </Button>
          </div>

          <MenuPopover className={"w-(--trigger-width) min-h-min"}>
            <Menu>
              {SORT_ORDER_OPTIONS.map((option) => (
                <MenuItem
                  key={`sort-order-drop-item-${option.value}`}
                  className={"text-xs tracking-wide pr-2"}
                  onAction={() =>
                    updateCreateEditNewsAlert({
                      ...createEditNewsAlert,
                      sections: new Map(
                        sectionList.map((section) => [
                          section.id,
                          {
                            ...section,
                            sortOrder: option.value as NewsAlertSortOrder, // Apply the sort order to all sections
                          },
                        ]),
                      ),
                    })
                  }
                >
                  {option.label}
                  {sortOrder === option.value && (
                    <CheckIcon className={"ml-auto"} />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </MenuPopover>
        </MenuTrigger>
      </div>
    );
  }, [sectionList]);

  return (
    <div className="flex flex-col gap-1">
      <Label
        aria-label="section-content-label"
        className="text-sm text-gray-700"
      >
        Configure Section Content
      </Label>

      <ul>
        {sectionList.map((section, index) => (
          // NOTE: Using the index as the key is a workaround to avoid the warning about using the name as the key.
          // In most cases the section will have an id, but if it doesn't (newly created sections before they are saved),
          // we use the index as the key.
          <NewsAlertsSectionListItem
            key={`section-list-item-${section.id || index}`}
          >
            <span className="flex flex-row gap-2 items-center">
              <Label
                aria-label="section-index-label"
                className="text-xs font-medium text-gray-900"
              >
                {index + 1}
              </Label>
              <Label
                aria-label="section-name-label"
                className="text-xs font-medium text-gray-900"
              >
                {section.name}
              </Label>
            </span>
            <span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  editNewsAlertSection(section);
                }}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={"text-red-600"}
                onClick={() => {
                  deleteNewsAlertSection(section.id);
                }}
              >
                Remove
              </Button>
            </span>
          </NewsAlertsSectionListItem>
        ))}
        <NewsAlertsSectionListItem
          key="section-list-item-add-section"
          borderLess
        >
          <Button
            variant="ghost"
            className={
              "flex flex-row grow-0 gap-2 h-12 items-center justify-start"
            }
            onClick={openCreateNewsAlertSectionModal}
          >
            <PlusIcon className="text-violet-500" size={16} />
            <span className="text-xs font-medium text-gray-900">
              Add a section
            </span>
          </Button>
        </NewsAlertsSectionListItem>
      </ul>
      <div>{renderArticleOptions()}</div>
    </div>
  );
};

export default NewsAlertsSectionsList;
